/* SO Illia - 2026-04-01: Third-pass keeps theme-side runtime work centralized so optional scripts and non-critical UI enhancements stay out of the initial critical path. */
(function() {
  const loadedScripts = new Map();
  const onceKeys = new Set();
  let reviewSliderObserver = null;

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function onWindowLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
    } else {
      window.addEventListener('load', callback, { once: true });
    }
  }

  function runOnIdle(callback, timeout) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: timeout || 2000 });
    } else {
      window.setTimeout(callback, timeout || 1);
    }
  }

  function runOnce(key, callback) {
    if (onceKeys.has(key)) {
      return;
    }

    onceKeys.add(key);
    callback();
  }

  function matchesPageGuard(pageGuard) {
    if (pageGuard === undefined) {
      return true;
    }

    if (typeof pageGuard === 'function') {
      try {
        return !!pageGuard();
      } catch (error) {
        return false;
      }
    }

    return !!pageGuard;
  }

  function loadScript(src, attributes) {
    if (!src) {
      return Promise.resolve(null);
    }

    if (loadedScripts.has(src)) {
      return loadedScripts.get(src);
    }

    const existingScript = document.querySelector('script[src="' + src + '"]');

    if (existingScript) {
      const existingPromise = Promise.resolve(existingScript);

      loadedScripts.set(src, existingPromise);
      return existingPromise;
    }

    const promise = new Promise(function(resolve, reject) {
      const script = document.createElement('script');

      script.src = src;
      script.async = attributes && attributes.async !== undefined ? attributes.async : true;

      if (attributes && attributes.defer) {
        script.defer = true;
      }

      if (attributes && attributes.id) {
        script.id = attributes.id;
      }

      if (attributes && attributes.crossOrigin) {
        script.crossOrigin = attributes.crossOrigin;
      }

      if (attributes && attributes.dataUuid) {
        script.dataset.uuid = attributes.dataUuid;
      }

      if (attributes && attributes.nowprocket) {
        script.setAttribute('nowprocket', '');
      }

      if (attributes && attributes.nitroExclude) {
        script.setAttribute('nitro-exclude', '');
      }

      script.onload = function() {
        resolve(script);
      };

      script.onerror = reject;

      document.head.appendChild(script);
    });

    loadedScripts.set(src, promise);
    return promise;
  }

  function scheduleAfterLoadOrInteraction(task, options) {
    let hasRun = false;
    const configuration = options || {};
    const interactionEvents = configuration.interactionEvents || ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    const interactionOptions = { once: true, passive: true, capture: true };
    const fallbackDelay = configuration.fallbackDelay === undefined ? 5000 : configuration.fallbackDelay;
    const idleTimeout = configuration.idleTimeout || 2000;

    function cleanup() {
      interactionEvents.forEach(function(eventName) {
        window.removeEventListener(eventName, runTask, interactionOptions);
      });
    }

    function runTask() {
      if (hasRun) {
        return;
      }

      hasRun = true;
      cleanup();
      runOnIdle(task, idleTimeout);
    }

    interactionEvents.forEach(function(eventName) {
      window.addEventListener(eventName, runTask, interactionOptions);
    });

    if (configuration.runOnLoad !== false) {
      onWindowLoad(function() {
        window.setTimeout(runTask, configuration.loadDelay || 0);
      });
    }

    if (fallbackDelay !== false) {
      window.setTimeout(runTask, fallbackDelay);
    }
  }

  function scheduleTaskByTrigger(task, configuration) {
    const trigger = configuration && configuration.trigger ? configuration.trigger : 'load';

    if (trigger === 'interaction') {
      scheduleAfterLoadOrInteraction(task, {
        runOnLoad: false,
        fallbackDelay: configuration.fallbackDelay === undefined ? 6000 : configuration.fallbackDelay,
        idleTimeout: configuration.idleTimeout || 2000
      });
      return;
    }

    if (trigger === 'idle') {
      runOnIdle(task, configuration && configuration.idleTimeout ? configuration.idleTimeout : 2000);
      return;
    }

    onWindowLoad(function() {
      runOnIdle(task, configuration && configuration.idleTimeout ? configuration.idleTimeout : 2000);
    });
  }

  function ensureJQuery() {
    if (window.jQuery) {
      return Promise.resolve(window.jQuery);
    }

    const jQueryUrl = window.theme && window.theme.assetUrls ? window.theme.assetUrls.jQueryUrl : '';

    return loadScript(jQueryUrl, { defer: true, crossOrigin: 'anonymous' }).then(function() {
      return window.jQuery;
    });
  }

  function ensureReviewSliderLibrary() {
    const slickUrl = window.theme && window.theme.assetUrls ? window.theme.assetUrls.slickUrl : '';

    return ensureJQuery().then(function(jQuery) {
      return loadScript(slickUrl, { defer: true }).then(function() {
        return jQuery;
      });
    });
  }

  function initReviewSliders() {
    if (!document.querySelector('.main-review-slider')) {
      return;
    }

    ensureReviewSliderLibrary().then(function($) {
      const desktopSettings = {
        slidesToShow: 2,
        slidesToScroll: 2,
        arrows: true,
        dots: false,
        infinite: true,
        adaptiveHeight: true,
        responsive: [
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 2,
              arrows: false
            }
          }
        ]
      };

      const mobileSettings = {
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: true,
        dots: false,
        infinite: true,
        adaptiveHeight: true,
        responsive: [
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 2,
              arrows: false
            }
          },
          {
            breakpoint: 767,
            settings: {
              slidesToShow: 1,
              arrows: true,
              dots: false
            }
          }
        ]
      };

      function destroySlider($slider) {
        if ($slider.hasClass('slick-initialized')) {
          $slider.slick('unslick');
        }
      }

      function ensureSlider($slider, settings) {
        if (!$slider.hasClass('slick-initialized')) {
          $slider.slick(settings);
        }
      }

      function syncReviewSliders() {
        const desktopSliderEnabled = window.matchMedia('(min-width: 999px)').matches;
        const mobileSliderEnabled = window.matchMedia('(max-width: 767px)').matches;

        $('.main-review-slider.desktop-slider').each(function() {
          const $slider = $(this);

          if (desktopSliderEnabled) {
            ensureSlider($slider, desktopSettings);
          } else {
            destroySlider($slider);
          }
        });

        $('.main-review-slider.mob-slider').each(function() {
          const $slider = $(this);

          if (mobileSliderEnabled) {
            ensureSlider($slider, mobileSettings);
          } else {
            destroySlider($slider);
          }
        });
      }

      syncReviewSliders();
      /* SO Illia - 2026-04-01: Bind the resize sync once because Shopify section reloads can call this initializer more than once. */
      if (!window.__soReviewSliderResizeBound) {
        window.addEventListener('resize', syncReviewSliders, { passive: true });
        window.__soReviewSliderResizeBound = true;
      }

      document.querySelectorAll('.main-review-slider').forEach(function(slider) {
        slider.classList.add('is-review-slider-ready');
      });
    }).catch(function() {
      // Intentionally noop so product pages continue working even if an optional slider asset fails.
    });
  }

  function queueReviewSliderInitialization() {
    const sliders = document.querySelectorAll('.main-review-slider');

    if (!sliders.length) {
      return;
    }

    function startReviewSliderInit() {
      if (reviewSliderObserver) {
        reviewSliderObserver.disconnect();
        reviewSliderObserver = null;
      }

      runOnIdle(initReviewSliders, 500);
    }

    /* SO Illia - 2026-04-01: Third-pass waits until review sliders approach the viewport before loading Slick so below-the-fold reviews stop consuming early main-thread time. */
    runOnce('review-slider-fallback', function() {
      scheduleAfterLoadOrInteraction(startReviewSliderInit, {
        runOnLoad: false,
        fallbackDelay: 8000,
        idleTimeout: 1000
      });
    });

    if (!('IntersectionObserver' in window)) {
      return;
    }

    if (!reviewSliderObserver) {
      reviewSliderObserver = new IntersectionObserver(function(entries) {
        if (entries.some(function(entry) { return entry.isIntersecting; })) {
          startReviewSliderInit();
        }
      }, {
        rootMargin: '250px 0px'
      });
    }

    sliders.forEach(function(slider) {
      if (slider.getAttribute('data-so-review-slider-observed') === 'true' || slider.classList.contains('slick-initialized')) {
        return;
      }

      slider.setAttribute('data-so-review-slider-observed', 'true');
      reviewSliderObserver.observe(slider);
    });
  }

  function applySafariProductBadgeLayout() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (!isSafari) {
      return;
    }

    document.querySelectorAll('.wd-product-badge').forEach(function(badge) {
      if (badge.hasAttribute('data-so-safari-badge-patched')) {
        return;
      }

      /* SO Illia - 2026-04-01: Second-pass moves the Safari badge patch out of head-inline product CSS so it only runs when badge markup actually exists. */
      badge.style.display = 'block';
      badge.style.flexWrap = 'wrap';
      badge.style.padding = '8px';
      badge.style.textAlign = 'center';
      badge.style.height = '28px';
      badge.style.flexDirection = 'column';
      badge.style.justifyContent = 'center';
      badge.style.alignItems = 'flex-start';
      badge.style.gap = '8px';
      badge.style.fontSize = '12px';
      badge.style.fontStyle = 'normal';
      badge.style.fontWeight = '600';
      badge.style.lineHeight = '12px';
      badge.setAttribute('data-so-safari-badge-patched', 'true');
    });
  }

  function enhanceSearchSubmitButton() {
    const searchButton = document.querySelector('.cm_search-box_submit.search-bar__submit');

    if (!searchButton || searchButton.getAttribute('data-so-search-icon-upgraded') === 'true') {
      return;
    }

    /* SO Illia - 2026-04-01: Second-pass keeps this Convermax search-button SVG swap out of head-inline JavaScript and only applies it after the search UI exists. */
    searchButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none"><g><path d="M19.4048 19.5L14.4048 14.5" stroke="#333333" stroke-width="2" stroke-linecap="square"/><path d="M8.90479 16.5C13.0469 16.5 16.4048 13.1421 16.4048 9C16.4048 4.85786 13.0469 1.5 8.90479 1.5C4.76265 1.5 1.40479 4.85786 1.40479 9C1.40479 13.1421 4.76265 16.5 8.90479 16.5Z" stroke="#333333" stroke-width="2"/></g></svg>';
    searchButton.setAttribute('data-so-search-icon-upgraded', 'true');
  }

  function applyMenuShowMore(dropdown, itemSelector, hiddenStartIndex, expandedClassName) {
    if (!dropdown || dropdown.getAttribute('data-so-menu-truncated') === 'true') {
      return;
    }

    const items = dropdown.querySelectorAll(itemSelector);

    if (items.length <= hiddenStartIndex + 1) {
      return;
    }

    items.forEach(function(item, index) {
      if (index > hiddenStartIndex) {
        item.hidden = true;
      }
    });

    const showMoreButton = document.createElement('button');

    showMoreButton.type = 'button';
    showMoreButton.className = 'nav-dropdown__show-more';
    showMoreButton.textContent = 'Show More';
    /* SO Illia - 2026-04-01: Second-pass defers menu truncation until after load or interaction so dropdown scanning stops competing with first render. */
    showMoreButton.addEventListener('click', function() {
      const isExpanding = showMoreButton.textContent === 'Show More';

      items.forEach(function(item, index) {
        if (index > hiddenStartIndex) {
          item.hidden = !isExpanding;
        }
      });

      showMoreButton.textContent = isExpanding ? 'Show Less' : 'Show More';

      if (expandedClassName) {
        dropdown.classList.toggle(expandedClassName, isExpanding);
      }
    });

    dropdown.appendChild(showMoreButton);
    dropdown.setAttribute('data-so-menu-truncated', 'true');
  }

  function initMenuShowMore() {
    document.querySelectorAll('.mobile-menu__nav:not(.wd-mobile-menu-heading)').forEach(function(dropdown) {
      applyMenuShowMore(dropdown, ':scope > .mobile-menu__nav-item:not(.wd-mobile-menu-header)', 8);
    });

    document.querySelectorAll('.nav-dropdown').forEach(function(dropdown) {
      applyMenuShowMore(dropdown, ':scope > .nav-dropdown__item', 9, 'nav-dropdown--expanded');
    });
  }

  function bindCartRefreshOnPageShow() {
    if (window.theme && window.theme.pageType === 'cart') {
      return;
    }

    /* SO Illia - 2026-04-01: Third-pass moves the bfcache cart refresh listener out of layout inline JavaScript so it stays deferred with the rest of the theme runtime. */
    runOnce('pageshow-cart-refresh', function() {
      window.addEventListener('pageshow', function() {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { scrollToTop: false }
        }));
      });
    });
  }

  function relocateStockistAppEmbed() {
    if (!window.location.href.includes('stockist-form')) {
      return;
    }

    const rootElem = document.querySelector('#main');
    const appEmbed = document.querySelector('.shopify-app-block #stockist-app-embed');

    if (!rootElem || !appEmbed || appEmbed.getAttribute('data-so-stockist-relocated') === 'true') {
      return;
    }

    /* SO Illia - 2026-04-01: Third-pass keeps the stockist form DOM move in deferred runtime instead of shipping another inline load handler in theme.liquid. */
    rootElem.appendChild(appEmbed);
    appEmbed.setAttribute('data-so-stockist-relocated', 'true');
  }

  const delayedExternalScriptDefinitions = {
    /* SO Illia - 2026-04-01: Console-fix restore keeps SEO Platform off the critical path while avoiding the extra inline head script that was reintroduced during rollback. */
    seoPlatform: {
      trigger: 'load',
      idleTimeout: 1200,
      load: function(externalScripts) {
        return loadScript(externalScripts.seoPlatformUrl, {
          dataCfasync: false,
          defer: true
        });
      }
    },
    waypointDynamicOptimization: {
      trigger: 'load',
      idleTimeout: 1500,
      load: function(externalScripts) {
        return loadScript(externalScripts.waypointDynamicOptimizationUrl, {
          id: 'sa-dynamic-optimization-loader',
          dataUuid: '72adba40-c898-421f-84ba-2601845f5702',
          nowprocket: true,
          nitroExclude: true
        });
      }
    },
    simpliFi: {
      trigger: 'load',
      idleTimeout: 2000,
      load: function(externalScripts) {
        return loadScript(externalScripts.simpliFiUrl, { async: true });
      }
    },
    manyChat: {
      trigger: 'interaction',
      idleTimeout: 1500,
      fallbackDelay: 7000,
      load: function(externalScripts) {
        return loadScript(externalScripts.manyChatWidgetUrl, { async: true }).then(function() {
          return loadScript(externalScripts.manyChatRuntimeUrl, { async: true });
        });
      }
    }
  };

  function loadDelayedExternalScript(scriptId) {
    const externalScripts = window.theme && window.theme.externalScripts ? window.theme.externalScripts : {};
    const definition = delayedExternalScriptDefinitions[scriptId];

    if (!definition || !matchesPageGuard(definition.pageGuard)) {
      return;
    }

    runOnce('delayed-external-script:' + scriptId, function() {
      Promise.resolve(definition.load(externalScripts)).catch(function() {
        return null;
      });
    });
  }

  function registerDelayedExternalScripts(scriptIds) {
    scriptIds.forEach(function(scriptId) {
      const definition = delayedExternalScriptDefinitions[scriptId];

      if (!definition || !matchesPageGuard(definition.pageGuard)) {
        return;
      }

      /* SO Illia - 2026-04-01: Third-pass keeps delayed app loading allowlist-based so only explicitly approved theme-controlled scripts can be shifted off the critical path. */
      runOnce('delayed-external-script-registration:' + scriptId, function() {
        scheduleTaskByTrigger(function() {
          loadDelayedExternalScript(scriptId);
        }, definition);
      });
    });
  }

  bindCartRefreshOnPageShow();
  onWindowLoad(relocateStockistAppEmbed);
  onReady(queueReviewSliderInitialization);
  document.addEventListener('shopify:section:load', function() {
    runOnIdle(queueReviewSliderInitialization, 300);
    runOnIdle(initReviewSliders, 500);
    runOnIdle(applySafariProductBadgeLayout, 500);
    runOnIdle(enhanceSearchSubmitButton, 500);
    runOnIdle(initMenuShowMore, 500);
  });
  onReady(applySafariProductBadgeLayout);

  document.addEventListener('click', function(event) {
    if (event.target.closest('[data-secondary-action="open-quick-view"]')) {
      window.setTimeout(applySafariProductBadgeLayout, 800);
    }
  });

  /* SO Illia - 2026-04-01: Second-pass migrates the old wd-js menu/search enhancements into deferred custom.js so they no longer execute inline in the head. */
  scheduleAfterLoadOrInteraction(function() {
    initMenuShowMore();
    enhanceSearchSubmitButton();
  });

  registerDelayedExternalScripts([
    /* SO Illia - 2026-04-01: Console-fix restore re-enables the delayed SEO Platform registration so vendor-side request noise does not re-enter the initial render path. */
    'seoPlatform',
    'waypointDynamicOptimization',
    'simpliFi',
    'manyChat'
  ]);
})();
